import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { setupSentryUser } from "Services/sentry.service";
import UserService from "Services/user.service";

import useActiveSinkId from "../components/VideoSurvey/hooks/useActiveSinkId/useActiveSinkId";
import useInputDeviceId from "../components/VideoSurvey/hooks/useInputDeviceId/useInputDeviceId";
import useActiveFlag from "../hooks/useActiveFlag";
import useActiveLanguage from "../hooks/useActiveLanguage";
import useCurrentBrand from "../hooks/useCurrentBrand";
import useCurrentCustomerUser from "../hooks/useCurrentCustomerUser";
import useCurrentUser from "../hooks/useCurrentUser";
import useDefaultLanguage from "../hooks/useDefaultLanguage";
import useLastSelectedOrderCustomerPortal from "../hooks/useLastSelectedOrderCustomerPortal";
import useLoginTime from "../hooks/useLoginTime";
import useSubDomainInfo from "../hooks/useSubDomainInfo";
import useUserUid from "../hooks/useUserUid";
import { CustomerUser } from "../models/customer.user";
import { User } from "../models/user";
import { Warehouse } from "../models/warehouse";
import { SubDomainInfo } from "../types";
import deleteAllTokens from "../util/deleteAllTokens";
import { CompanyProfile } from "../util/enums";
import { RolePermissionsResponse } from "Models/resources";
import { Timezone } from "Models/timezone";
import { formatInTimeZone } from "date-fns-tz";
import { getDateFnsLocale } from "../util/getDateFnsLocale";
import { SELECTED_LANGUAGE_PREFIX } from "../constants";
import { CompanyConfigSettingsSchemaInterface } from "Models/company.config";
import { NetworkConfiguration } from "../pages/private/Shared/Settings/Configurations/General/Network/types";

const DEFAULT_GLOBAL_TIMEZONE = {
  id: 64,
  valid: true,
  name: "America/Bahia",
  nameFormatted: "Brazil/Bahia",
  gmtOffset: -3,
  countryCode: "BR",
  createdAt: new Date("2021-03-09T19:01:00.000Z"),
  updatedAt: new Date("2025-06-02T23:23:01.000Z"),
} as Timezone;

export interface StateContextType {
  activeLanguageId: number;
  setActiveLanguageId(languageId: number): void;
  defaultLanguage: number;
  setDefaultLanguage(languageId: number): void;
  mapIsLoaded: boolean;
  setMapIsLoaded(loaded: boolean): void;
  activeSinkId: string;
  setActiveSinkId(sinkId: string): void;
  activeFlag: string;
  setActiveFlag(flag: string): void;
  inputDeviceId: string;
  setInputDeviceId(deviceId: string): void;
  isDumontSidebarOpen: boolean;
  setIsDumontSidebarOpen(isOpen: boolean): void;
  currentUser?: User & {
    totalCompaniesWithNetwork?: number;
  };
  setCurrentUser(user: User): void;
  currentCustomerUser?: CustomerUser;
  setCurrentCustomerUser(CustomerUser?: CustomerUser): void;
  logoutCustomerUser: () => void;
  userUid?: string;
  setUserUid(userUid: string): void;
  userLogout: () => void;
  loginTime?: Date;
  setLoginTime(): void;
  unreadTotal: number;
  setUnreadTotal(total: number): void;
  warehouse?: Warehouse;
  setWarehouse(warehouse?: Warehouse): void;
  warehouseList?: Warehouse[];
  setWarehouseList(warehouseList?: Warehouse[]): void;
  hasUserChanged: boolean;
  setHasUserChanged(userChanged: boolean): void;
  isLogoutRequired?: boolean;
  setIsLogoutRequired(isLogoutRequired?: boolean): void;
  defaultTimezone: string;

  companyTimezone: Timezone;
  formatWithCompanyTimezone: (date: Date | string | number, formatText: string) => string;

  userTimezone: Timezone;
  formatWithUserTimezone: (date: Date | string | number, formatText: string) => string;

  hasUnsavedChanges: boolean;
  setHasUnsavedChanges(hasUnsavedChanges: boolean): void;

  subDomainInfo?: SubDomainInfo;
  setSubDomainInfo(subDomainInfo?: SubDomainInfo): void;

  lastOrderSelectedId?: number;
  setLastOrderSelectedId(orderId?: number): void;

  loadingPermission?: boolean;
  setLoadingPermission(loaded: boolean): void;

  pathPermissions?: { route: string; isShow: boolean }[];
  setPathPermissions(permissions?: { route: string; isShow: boolean }[]): void;

  userPermissions?: RolePermissionsResponse[];
  setUserPermissions(permissions?: RolePermissionsResponse[]): void;

  agentPermissions?: { [jobId: number]: NetworkConfiguration };
  setAgentPermissions(permissions?: { [jobId: number]: NetworkConfiguration }): void;

  agentCompany?: number;
  setAgentCompany(companyId?: number): void;

  statusGroupGrid?: any[];
  setStatusGroupGrid(status: any[]): void;
  statusAccessRuleGrid?: any[];
  setStatusAccessRuleGrid(status: any[]): void;

  isSupplierPortal: boolean;
  isFMVersion: boolean;
  isSPVersion: boolean;
  isFMorSPVersion: boolean;
  isMovingVersion: boolean;
  isSTVersion: boolean;
  isMovingAgentVersion: boolean;
}

export const StateContext = createContext<StateContextType>(null!);

export default function AppStateProvider(props: React.PropsWithChildren<{}>) {
  const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [activeLanguageId, setActiveLanguageId] = useActiveLanguage();
  const [defaultLanguage, setDefaultLanguage] = useDefaultLanguage();
  const [activeFlag, setActiveFlag] = useActiveFlag();
  const [activeSinkId, setActiveSinkId] = useActiveSinkId();
  const [inputDeviceId, setInputDeviceId] = useInputDeviceId();
  const [currentUser, setCurrentUser] = useCurrentUser();
  const [statusGroupGrid, setStatusGroupGrid] = useState<string[]>([]);
  const [statusAccessRuleGrid, setStatusAccessRuleGrid] = useState<string[]>([]);
  const [isDumontSidebarOpen, setIsDumontSidebarOpen] = useState<boolean>(false);

  const [userUid, setUserUid] = useUserUid();
  const [mapIsLoaded, setMapIsLoaded] = useState<boolean>(false);
  const [loginTime, setLoginTime] = useLoginTime();
  const [unreadTotal, setUnreadTotal] = useState<number>(0);
  const [warehouse, setWarehouse] = useState<Warehouse>();
  const [warehouseList, setWarehouseList] = useState<Warehouse[]>();
  const [hasUserChanged, setHasUserChanged] = useState(true);
  const [isLogoutRequired, setIsLogoutRequired] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [loadingPermission, setLoadingPermission] = useState<boolean>(true);
  const [pathPermissions, setPathPermissions] = useState<{ route: string; isShow: boolean }[]>([]);
  const [userPermissions, setUserPermissions] = useState<RolePermissionsResponse[]>([]);

  const [agentPermissions, setAgentPermissions] =
    useState<CompanyConfigSettingsSchemaInterface["networkConfiguration"]>();

  const [agentCompany, setAgentCompany] = useState<number>();

  const [currentCustomerUser, setCurrentCustomerUser] = useCurrentCustomerUser();
  const [subDomainInfo, setSubDomainInfo] = useSubDomainInfo();
  const [lastOrderSelectedId, setLastOrderSelectedId] = useLastSelectedOrderCustomerPortal();

  const isSupplierPortal = currentUser?.company?.profile?.initials === CompanyProfile.SPP;
  const isFMVersion = currentUser?.company?.profile?.initials === CompanyProfile.FM;
  const isSPVersion = currentUser?.company?.profile?.initials === CompanyProfile.SP;
  const isFMorSPVersion = isSPVersion || isFMVersion;
  const isMovingVersion = currentUser?.company?.profile?.initials === CompanyProfile.MV;
  const isSTVersion = currentUser?.company?.profile?.initials === CompanyProfile.ST;
  const isMovingAgentVersion = currentUser?.company?.profile?.initials === CompanyProfile.MA;

  const companyTimezone: Timezone = useMemo(
    () => currentUser?.company?.timezone ?? DEFAULT_GLOBAL_TIMEZONE,
    [currentUser?.company?.timezone]
  );
  const userTimezone: Timezone = useMemo(
    () => currentUser?.timezone ?? DEFAULT_GLOBAL_TIMEZONE,
    [currentUser?.timezone]
  );

  const languagePrefix = localStorage.getItem(SELECTED_LANGUAGE_PREFIX) || "en";
  const dateFnsLocale = useMemo(() => getDateFnsLocale(languagePrefix), [languagePrefix]);

  const capitalizeWords = (str: string) =>
    str.replace(/(^|\s)([a-záéíóúàèìòùâêîôûãõñçüäöå])/g, (_, space, char) =>
      space + char.toUpperCase()
    );

  const formatWithCompanyTimezone = useCallback(
    (date: Date | string | number, formatText: string): string => {
      if (!date) return "";

      const timezoneName = companyTimezone.name || Intl.DateTimeFormat().resolvedOptions().timeZone;
      return capitalizeWords(
        formatInTimeZone(date, timezoneName, formatText, { locale: dateFnsLocale })
      );
    },
    [companyTimezone.name, dateFnsLocale]
  );

  const formatWithUserTimezone = useCallback(
    (date: Date | string | number, formatText: string): string => {
      if (!date) return "";

      const timezoneName = userTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

      return capitalizeWords(
        formatInTimeZone(date, timezoneName, formatText, { locale: dateFnsLocale })
      );
    },
    [userTimezone?.name, dateFnsLocale]
  );

  const { setCurrentBrand } = useCurrentBrand();

  /*
  The 'react-hooks/rules-of-hooks' linting rules prevent React Hooks from being called
  inside of if() statements. This is because hooks must always be called in the same order
  every time a component is rendered. The 'react-hooks/rules-of-hooks' rule is disabled below
  because the "if (process.env.REACT_APP_SET_AUTH === 'firebase')" statements are evaluated
  at build time (not runtime). If the statement evaluates to false, then the code is not
  included in the bundle that is produced (due to tree-shaking). Thus, in this instance, it
  is ok to call hooks inside if() statements.
  */

  const userLogout = async () => {
    if (currentUser?.id && currentUser?.company?.id) {
      try {
        await UserService.logout({
          userId: currentUser.id,
          companyId: currentUser.company.id,
          logoutFrom: "web",
        });
      } catch (error) {
        console.error("Error calling logout API:", error);
      }
    }

    setCurrentBrand(undefined);
    setCurrentUser(undefined);
    setUserUid(undefined);
    deleteAllTokens();
  };

  const logoutCustomerUser = useCallback(() => {
    setCurrentCustomerUser(undefined);
    deleteAllTokens({ shouldReload: false });
  }, [setCurrentCustomerUser]);

  useEffect(() => {
    setupSentryUser(currentUser);
  }, [currentUser]);

  const contextValue = {
    mapIsLoaded,
    setMapIsLoaded,
    currentUser,
    setCurrentUser,
    userUid,
    setUserUid,
    userLogout,
    activeLanguageId,
    setActiveLanguageId,
    defaultLanguage,
    setDefaultLanguage,
    activeFlag,
    setActiveFlag,
    activeSinkId,
    setActiveSinkId,
    inputDeviceId,
    setInputDeviceId,
    isDumontSidebarOpen,
    setIsDumontSidebarOpen,
    loginTime,
    setLoginTime,
    isSupplierPortal,
    isFMVersion,
    unreadTotal,
    setUnreadTotal,
    warehouse,
    setWarehouse,
    warehouseList,
    setWarehouseList,
    isSPVersion,
    isFMorSPVersion,
    isMovingVersion,
    isMovingAgentVersion,
    isSTVersion,
    hasUserChanged,
    setHasUserChanged,
    isLogoutRequired,
    setIsLogoutRequired,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    subDomainInfo,
    setSubDomainInfo,
    currentCustomerUser,
    setCurrentCustomerUser,
    logoutCustomerUser,
    lastOrderSelectedId,
    setLastOrderSelectedId,
    pathPermissions,
    setPathPermissions,
    userPermissions,
    setUserPermissions,
    statusGroupGrid,
    setStatusGroupGrid,
    statusAccessRuleGrid,
    setStatusAccessRuleGrid,
    companyTimezone,
    formatWithCompanyTimezone,
    userTimezone,
    formatWithUserTimezone,
    defaultTimezone,
    loadingPermission,
    setLoadingPermission,
    agentPermissions,
    setAgentPermissions,
    agentCompany,
    setAgentCompany,
  } as StateContextType;

  return (
    <StateContext.Provider value={{ ...contextValue }}>{props.children}</StateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(StateContext);

  if (!context) {
    throw new Error("useAppState must be used within the AppStateProvider");
  }

  return context;
}
